import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Language, View } from '../../types';
import { fadeInUp, gentleReveal } from '../../lib/animations';

interface AboutTeaserProps {
  lang: Language;
  setView: (v: View) => void;
}

const AboutTeaser: React.FC<AboutTeaserProps> = ({ lang, setView }) => {
  const content = {
    label: {
      en: "Meet Kayvan",
      fa: "با کیوان آشنا شو"
    },
    bio: {
      en: "For over a decade, I've explored the inner landscape—from Buddhist temple wisdom to modern psychology. I help people transform inner conflict into clarity and courage.",
      fa: "بیشتر از ده سال از معابد بودایی تا روانشناسی مدرن رو کاوش کردم. کمک می‌کنم تضادهای درونی به شفافیت و شجاعت تبدیل بشه."
    },
    link: {
      en: "Read my full story",
      fa: "داستان کامل من"
    }
  };

  // Split the bio to add italic emphasis on "inner landscape" / "درونی"
  const renderBio = () => {
    if (lang === 'en') {
      const parts = content.bio.en.split('inner landscape');
      return (
        <>
          {parts[0]}<span className="italic">inner landscape</span>{parts[1]}
        </>
      );
    }
    return content.bio.fa;
  };

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${lang === 'fa' ? 'lg:grid-flow-dense' : ''}`}>
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: lang === 'fa' ? 30 : -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.4 }}
            className={`relative ${lang === 'fa' ? 'lg:col-start-2' : ''}`}
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-gray-100 shadow-xl">
              <img
                src="./kayvan.png"
                alt="Kayvan"
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1544168190-79c17527004f?auto=format&fit=crop&q=80&w=1000";
                }}
              />
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className={`space-y-5 ${lang === 'fa' ? 'lg:col-start-1' : ''}`}
          >
            {/* Label */}
            <span className="text-xs uppercase tracking-wider font-bold text-primary">
              {content.label[lang]}
            </span>

            {/* Bio */}
            <p className="font-serif text-2xl md:text-3xl leading-relaxed text-brandDark text-pretty">
              {renderBio()}
            </p>

            {/* Link */}
            <button
              onClick={() => setView('about')}
              className="group inline-flex items-center gap-2 text-primary font-medium text-sm hover:text-teal-700 transition-colors duration-200 min-h-[44px] py-2"
            >
              {content.link[lang]}
              <ArrowRight className={`w-4 h-4 ${lang === 'fa' ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'} transition-transform duration-200`} />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutTeaser;
